import React, {useCallback, useMemo, useState, useEffect} from 'react'
import './ModalFacilityEdit.css'
import { getCurrentUser } from '../utils/auth'
import { BsFillPencilFill, BsFillTrashFill, BsPlusLg } from 'react-icons/bs'

const PHONE_TYPE_OPTIONS = [
    { id: 1, label: 'Mobile' },
    { id: 2, label: 'Home' },
    { id: 3, label: 'Work' },
    { id: 4, label: 'Facility' },
    { id: 5, label: 'Corporate' },
];

const STATE_OPTIONS = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const REQUEST_HEADERS = {
    'machine':'2',
    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
};

const toValidId = (value) => {
    const asNumber = Number(value);
    return Number.isInteger(asNumber) && asNumber > 0 ? asNumber : null;
};

const parseFacilityIdFromCreateResponse = (data) => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const candidates = [
        data.id,
        data.facility_id,
        data.facilityId,
        data?.facility?.id,
        data?.facility?.facility_id,
        data?.data?.id,
        data?.data?.facility_id,
        data?.result?.id,
        data?.result?.facility_id,
    ];

    for (const candidate of candidates) {
        const parsed = toValidId(candidate);
        if (parsed) {
            return parsed;
        }
    }

    return null;
};

const ModalFacilityEdit = ({closeModal, onSubmit, facilityEdit, isNewFacility, allFacilityTypes, allCustomers, isReadOnly = false}) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenUserType = tokenPayload?.user_type;
    const isReadOnlyMode = !!isReadOnly;

    const [facilityInfo, setFacilityInfo] = useState(facilityEdit);
    const [customerSelectDisabled, setCustomerSelectDisabled] = useState(false);
    const [facilityTypeSelectDisabled, setFacilityTypeSelectDisabled] = useState(false);
    const [phones, setPhones] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [addressTypes, setAddressTypes] = useState([]);
    const [isLoadingModalData, setIsLoadingModalData] = useState(true);
    const [errors, setErrors] = useState('');

    const [showPhoneRemoveConfirm, setShowPhoneRemoveConfirm] = useState(false);
    const [phonePendingDelete, setPhonePendingDelete] = useState(null);
    const [showPhoneEditor, setShowPhoneEditor] = useState(false);
    const [phoneEditorState, setPhoneEditorState] = useState({
        mode: 'add',
        phoneId: null,
        localId: null,
        phoneTypeId: 1,
        phone: '',
    });

    const [showAddressRemoveConfirm, setShowAddressRemoveConfirm] = useState(false);
    const [addressPendingDelete, setAddressPendingDelete] = useState(null);
    const [showAddressEditor, setShowAddressEditor] = useState(false);
    const [addressEditorState, setAddressEditorState] = useState({
        mode: 'add',
        addressId: null,
        localId: null,
        addressTypeId: 5,
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
    });

    const selectedFacilityTypeValue = useMemo(() => {
        const directTypeId = facilityInfo?.facility_type_id ?? facilityInfo?.facility_type;
        if (directTypeId !== undefined && directTypeId !== null && directTypeId !== '') {
            return String(directTypeId);
        }

        const facilityDescription = String(facilityInfo?.description ?? '').trim().toLowerCase();
        if (!facilityDescription) {
            return '';
        }

        const match = allFacilityTypes.find((facilityType) => String(facilityType.description ?? '').trim().toLowerCase() === facilityDescription);
        const matchedId = match?.id ?? match?.facility_type_id;
        return matchedId !== undefined && matchedId !== null ? String(matchedId) : '';
    }, [facilityInfo, allFacilityTypes]);

    const selectedCustomerValue = useMemo(() => {
        const directCustomerId = facilityInfo?.customer_id ?? facilityInfo?.customer;
        if (directCustomerId !== undefined && directCustomerId !== null && directCustomerId !== '') {
            return String(directCustomerId);
        }

        const customerName = String(facilityInfo?.customer_name ?? '').trim().toLowerCase();
        if (!customerName) {
            return '';
        }

        const match = allCustomers.find((customer) => String(customer.customer_name ?? '').trim().toLowerCase() === customerName);
        const matchedId = match?.id ?? match?.customer_id;
        return matchedId !== undefined && matchedId !== null ? String(matchedId) : '';
    }, [facilityInfo, allCustomers]);

    const fetchFacilityPhones = useCallback(async (facilityId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_phones/facility/' + facilityId,
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setPhones(Array.isArray(data) ? data : []);
    }, []);

    const fetchFacilityAddresses = useCallback(async (facilityId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_addresses/facility/' + facilityId,
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setAddresses(Array.isArray(data) ? data : []);
    }, []);

    const fetchAddressTypes = useCallback(async () => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/address_types/',
            {
                method: 'GET',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        const data = await response.json();
        setAddressTypes(Array.isArray(data) ? data : []);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadModalData = async () => {
            setIsLoadingModalData(true);

            try {
                if (tokenUserType < 3) {
                    setCustomerSelectDisabled(true);
                    setFacilityTypeSelectDisabled(true);
                }

                await fetchAddressTypes();

                if (isNewFacility) {
                    if (!isMounted) {
                        return;
                    }

                    setAddresses([]);
                    setPhones([]);
                    return;
                }

                await Promise.all([
                    fetchFacilityAddresses(facilityInfo.id),
                    fetchFacilityPhones(facilityInfo.id),
                ]);
            }
            catch (error) {
                console.error(error);
            }
            finally {
                if (isMounted) {
                    setIsLoadingModalData(false);
                }
            }
        };

        loadModalData();

        return () => {
            isMounted = false;
        };
    }, [facilityInfo.id, fetchAddressTypes, fetchFacilityAddresses, fetchFacilityPhones, isNewFacility, tokenUserType]);

    const apiPatchFacilityInfo = async (facilityId, patchFacilityInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facilities/facility/' + facilityId,
            {
                method: 'PATCH',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchFacilityInfo),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        await response.json();
    };

    const apiPostNewFacility = async (patchFacilityInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facilities/',
            {
                method: 'POST',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchFacilityInfo),
            });

        const rawResponse = await response.text();
        let parsedResponse = null;
        try {
            parsedResponse = rawResponse ? JSON.parse(rawResponse) : null;
        } catch (error) {
            parsedResponse = null;
        }

        if (!response.ok) {
            const backendError = parsedResponse?.detail
                || parsedResponse?.message
                || parsedResponse?.error
                || `Unable to create facility. (${response.status})`;

            return { success: false, facilityId: null, error: String(backendError) };
        }

        const facilityId = parseFacilityIdFromCreateResponse(parsedResponse);
        return { success: true, facilityId, error: null };
    };

    const apiCreatePhone = async (facilityId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_phones/',
            {
                method: 'POST',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify({
                    facility: Number(facilityId),
                    phone_type: Number(payload.phone_type),
                    phone: String(payload.phone ?? ''),
                }),
            });

        if (!response.ok) {
            const rawResponse = await response.text();
            let parsedResponse = null;
            try {
                parsedResponse = rawResponse ? JSON.parse(rawResponse) : null;
            } catch (error) {
                parsedResponse = null;
            }

            const backendError = parsedResponse?.errors
                || parsedResponse?.detail
                || parsedResponse?.message
                || parsedResponse?.error
                || `Fetch error - ${response.url} - Status: ${response.status}`;

            throw new Error(String(backendError));
        }

        return response.json();
    };

    const apiUpdatePhone = async (phoneId, facilityId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_phones/facility_phone/' + phoneId,
            {
                method: 'PATCH',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify({
                    facility: Number(facilityId),
                    phone_type: Number(payload.phone_type),
                    phone: String(payload.phone ?? ''),
                }),
            });

        if (!response.ok) {
            const rawResponse = await response.text();
            let parsedResponse = null;
            try {
                parsedResponse = rawResponse ? JSON.parse(rawResponse) : null;
            } catch (error) {
                parsedResponse = null;
            }

            const backendError = parsedResponse?.errors
                || parsedResponse?.detail
                || parsedResponse?.message
                || parsedResponse?.error
                || `Fetch error - ${response.url} - Status: ${response.status}`;

            throw new Error(String(backendError));
        }

        return response.json();
    };

    const apiDeletePhone = async (phoneId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_phones/facility_phone/' + phoneId,
            {
                method: 'DELETE',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }
    };

    const apiCreateAddress = async (facilityId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_addresses/',
            {
                method: 'POST',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify({
                    facility: Number(facilityId),
                    address_type: Number(payload.address_type),
                    street_1: String(payload.street_1 ?? ''),
                    street_2: String(payload.street_2 ?? ''),
                    city: String(payload.city ?? ''),
                    st: String(payload.st ?? ''),
                    zip: String(payload.zip ?? ''),
                    country: String(payload.country ?? 'United States'),
                }),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiUpdateAddress = async (addressId, facilityId, payload) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_addresses/facility_address/' + addressId,
            {
                method: 'PATCH',
                headers: {
                    ...REQUEST_HEADERS,
                    'content-type': 'application/json',
                },
                body: JSON.stringify({
                    facility: Number(facilityId),
                    address_type: Number(payload.address_type),
                    street_1: String(payload.street_1 ?? ''),
                    street_2: String(payload.street_2 ?? ''),
                    city: String(payload.city ?? ''),
                    st: String(payload.st ?? ''),
                    zip: String(payload.zip ?? ''),
                    country: String(payload.country ?? 'United States'),
                }),
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }

        return response.json();
    };

    const apiDeleteAddress = async (addressId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/facility_addresses/facility_address/' + addressId,
            {
                method: 'DELETE',
                headers: REQUEST_HEADERS,
            });

        if (!response.ok) {
            throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
        }
    };

    const extractPhoneDigits = (value) => String(value ?? '').replace(/\D/g, '').slice(0, 10);

    const formatPhoneInput = (value) => {
        const digits = extractPhoneDigits(value);

        if (digits.length === 0) {
            return '';
        }

        if (digits.length <= 3) {
            return `(${digits}`;
        }

        if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        }

        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    const formatPhoneNumber = (phone) => formatPhoneInput(phone);

    const getPhoneTypeId = (phone) => {
        if (!phone) {
            return 1;
        }

        if (phone.phone_type_id) {
            return Number(phone.phone_type_id);
        }

        if (typeof phone.phone_type === 'number') {
            return Number(phone.phone_type);
        }

        if (typeof phone.phone_type === 'object' && phone.phone_type !== null) {
            return Number(phone.phone_type.phone_type_id ?? phone.phone_type.id ?? 1);
        }

        const normalizedType = String(phone.phone_type ?? '').trim().toLowerCase();
        const matched = PHONE_TYPE_OPTIONS.find((option) => option.label.toLowerCase() === normalizedType);
        return matched ? matched.id : 1;
    };

    const getPhoneTypeLabel = (phone) => {
        if (!phone) {
            return 'Phone';
        }

        if (typeof phone.phone_type === 'object' && phone.phone_type !== null) {
            return phone.phone_type.phone_type || phone.phone_type.description || 'Phone';
        }

        if (typeof phone.phone_type === 'string') {
            return phone.phone_type;
        }

        const typeId = phone.phone_type_id || phone.phone_type;
        const matched = PHONE_TYPE_OPTIONS.find((option) => option.id === Number(typeId));
        return matched?.label || 'Phone';
    };

    const addressTypeOptions = useMemo(() => {
        if (Array.isArray(addressTypes) && addressTypes.length > 0) {
            return addressTypes
                .map((addressType) => ({
                    id: Number(addressType?.address_type_id ?? addressType?.id),
                    label: String(addressType?.address_type ?? addressType?.description ?? addressType?.name ?? '').trim(),
                }))
                .filter((addressType) => Number.isInteger(addressType.id) && addressType.label);
        }

        return [
            { id: 1, label: 'Home' },
            { id: 2, label: 'Work' },
            { id: 3, label: 'Shipping' },
            { id: 4, label: 'Billing' },
            { id: 5, label: 'Facility' },
            { id: 6, label: 'Corporate Office' },
        ];
    }, [addressTypes]);

    const getAddressTypeId = (address) => {
        if (!address) {
            return 5;
        }

        if (address.address_type_id) {
            return Number(address.address_type_id);
        }

        if (typeof address.address_type === 'number') {
            return Number(address.address_type);
        }

        if (typeof address.address_type === 'object' && address.address_type !== null) {
            return Number(address.address_type.address_type_id ?? address.address_type.id ?? 5);
        }

        return 5;
    };

    const getAddressTypeLabel = (address) => {
        if (!address) {
            return 'Address';
        }

        if (typeof address.address_type === 'object' && address.address_type !== null) {
            return address.address_type.address_type || address.address_type.description || 'Address';
        }

        if (typeof address.address_type === 'string') {
            return address.address_type;
        }

        const typeId = Number(address.address_type_id ?? address.address_type);
        const matched = addressTypeOptions.find((option) => option.id === typeId);
        return matched?.label || 'Address';
    };

    const formatAddressPreview = (address) => {
        const lineOne = String(address?.street_1 ?? '').trim();
        const lineTwo = String(address?.street_2 ?? '').trim();
        const city = String(address?.city ?? '').trim();
        const state = String(address?.st ?? '').trim();
        const zip = String(address?.zip ?? '').trim();

        return [
            [lineOne, lineTwo].filter(Boolean).join(' '),
            [city, state, zip].filter(Boolean).join(', ').replace(', ,', ', '),
        ].filter(Boolean);
    };

    const validateForm = async () => {
        if (String(facilityInfo.facility_name ?? '').trim()) {
            setErrors('');
            return true;
        }

        setErrors('facility_name');
        return false;
    };

    const handleChange = (e) => {
        setFacilityInfo({
            ...facilityInfo,
            [e.target.name]: e.target.value
        });
    };

    const handleTypeChange = (e) => {
        const selectedTypeId = Number(e.target.value);
        const selectedType = allFacilityTypes.find((facilityType) => {
            const typeId = facilityType.id ?? facilityType.facility_type_id;
            return Number(typeId) === selectedTypeId;
        });
        if (!selectedType) {
            return;
        }

        setFacilityInfo({
            ...facilityInfo,
            description: selectedType.description,
            facility_type_id: selectedTypeId,
            facility_type: selectedTypeId,
        });
    };

    const handleCustomerChange = (e) => {
        const selectedCustomerId = Number(e.target.value);
        const selectedCustomer = allCustomers.find((customer) => {
            const customerId = customer.id ?? customer.customer_id;
            return Number(customerId) === selectedCustomerId;
        });
        if (!selectedCustomer) {
            return;
        }

        setFacilityInfo({
            ...facilityInfo,
            customer_name: selectedCustomer.customer_name,
            customer_id: selectedCustomerId,
            customer: selectedCustomerId,
        });
    };

    const handleCheckChange = (e) => {
        setFacilityInfo({
            ...facilityInfo,
            [e.target.name]: e.target.checked
        });
    };

    const getFacilityIdOrNull = () => {
        const id = Number(facilityInfo?.id);
        return Number.isInteger(id) ? id : null;
    };

    const openAddPhoneModal = () => {
        setPhoneEditorState({
            mode: 'add',
            phoneId: null,
            localId: null,
            phoneTypeId: 1,
            phone: '',
        });
        setShowPhoneEditor(true);
    };

    const openEditPhoneModal = (phone) => {
        setPhoneEditorState({
            mode: 'edit',
            phoneId: Number(phone?.facility_phone_id),
            localId: phone?.local_id ?? null,
            phoneTypeId: getPhoneTypeId(phone),
            phone: formatPhoneInput(phone?.phone ?? phone?.phone_number),
        });
        setShowPhoneEditor(true);
    };

    const handlePhoneEditorCancel = () => {
        setShowPhoneEditor(false);
        setPhoneEditorState({
            mode: 'add',
            phoneId: null,
            localId: null,
            phoneTypeId: 1,
            phone: '',
        });
    };

    const handlePhoneEditorTypeChange = (event) => {
        setPhoneEditorState((previous) => ({
            ...previous,
            phoneTypeId: Number(event.target.value),
        }));
    };

    const handlePhoneEditorPhoneChange = (event) => {
        setPhoneEditorState((previous) => ({
            ...previous,
            phone: formatPhoneInput(event.target.value),
        }));
    };

    const handlePhoneEditorSave = async () => {
        const phoneDigits = extractPhoneDigits(phoneEditorState.phone);
        if (phoneDigits.length !== 10) {
            return;
        }

        const payload = {
            phone_type: Number(phoneEditorState.phoneTypeId),
            phone: phoneDigits,
        };

        const facilityId = getFacilityIdOrNull();

        try {
            if (!facilityId || isNewFacility) {
                if (phoneEditorState.mode === 'add') {
                    setPhones((previous) => ([
                        ...previous,
                        {
                            local_id: Date.now(),
                            phone_type: payload.phone_type,
                            phone: payload.phone,
                        },
                    ]));
                } else {
                    setPhones((previous) => previous.map((phone) => {
                        const hasServerId = Number(phone?.facility_phone_id) === Number(phoneEditorState.phoneId);
                        const hasLocalId = phone?.local_id === phoneEditorState.localId;
                        if (!hasServerId && !hasLocalId) {
                            return phone;
                        }
                        return {
                            ...phone,
                            phone_type: payload.phone_type,
                            phone: payload.phone,
                        };
                    }));
                }

                handlePhoneEditorCancel();
                return;
            }

            if (phoneEditorState.mode === 'add') {
                await apiCreatePhone(facilityId, payload);
            } else if (phoneEditorState.phoneId) {
                await apiUpdatePhone(phoneEditorState.phoneId, facilityId, payload);
            }

            await fetchFacilityPhones(facilityId);
            handlePhoneEditorCancel();
        } catch (error) {
            console.error(error);
        }
    };

    const openRemovePhoneDialog = (phone) => {
        setPhonePendingDelete(phone);
        setShowPhoneRemoveConfirm(true);
    };

    const handleCancelRemovePhoneDialog = () => {
        setShowPhoneRemoveConfirm(false);
        setPhonePendingDelete(null);
    };

    const handleConfirmRemovePhone = async () => {
        const facilityId = getFacilityIdOrNull();
        const phoneId = Number(phonePendingDelete?.facility_phone_id);

        try {
            if (!facilityId || isNewFacility || !phoneId) {
                setPhones((previous) => previous.filter((phone) => phone !== phonePendingDelete));
            } else {
                await apiDeletePhone(phoneId);
                await fetchFacilityPhones(facilityId);
            }
        } catch (error) {
            console.error(error);
        } finally {
            handleCancelRemovePhoneDialog();
        }
    };

    const openAddAddressModal = () => {
        setAddressEditorState({
            mode: 'add',
            addressId: null,
            localId: null,
            addressTypeId: 5,
            street1: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
        });
        setShowAddressEditor(true);
    };

    const openEditAddressModal = (address) => {
        setAddressEditorState({
            mode: 'edit',
            addressId: Number(address?.facility_address_id),
            localId: address?.local_id ?? null,
            addressTypeId: getAddressTypeId(address),
            street1: String(address?.street_1 ?? ''),
            street2: String(address?.street_2 ?? ''),
            city: String(address?.city ?? ''),
            state: String(address?.st ?? '').toUpperCase(),
            zip: String(address?.zip ?? ''),
        });
        setShowAddressEditor(true);
    };

    const handleAddressEditorCancel = () => {
        setShowAddressEditor(false);
        setAddressEditorState({
            mode: 'add',
            addressId: null,
            localId: null,
            addressTypeId: 5,
            street1: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
        });
    };

    const handleAddressEditorChange = (field, value) => {
        setAddressEditorState((previous) => ({
            ...previous,
            [field]: field === 'state' ? String(value).toUpperCase() : value,
        }));
    };

    const isAddressEditorValid = () => String(addressEditorState.street1).trim() !== ''
        && String(addressEditorState.city).trim() !== ''
        && String(addressEditorState.state).trim() !== ''
        && String(addressEditorState.zip).trim() !== '';

    const handleAddressEditorSave = async () => {
        if (!isAddressEditorValid()) {
            return;
        }

        const payload = {
            address_type: Number(addressEditorState.addressTypeId),
            street_1: String(addressEditorState.street1 ?? '').trim(),
            street_2: String(addressEditorState.street2 ?? '').trim(),
            city: String(addressEditorState.city ?? '').trim(),
            st: String(addressEditorState.state ?? '').toUpperCase(),
            zip: String(addressEditorState.zip ?? '').trim(),
            country: 'United States',
        };

        const facilityId = getFacilityIdOrNull();

        try {
            if (!facilityId || isNewFacility) {
                if (addressEditorState.mode === 'add') {
                    setAddresses((previous) => ([
                        ...previous,
                        {
                            local_id: Date.now(),
                            address_type: payload.address_type,
                            street_1: payload.street_1,
                            street_2: payload.street_2,
                            city: payload.city,
                            st: payload.st,
                            zip: payload.zip,
                            country: payload.country,
                        },
                    ]));
                } else {
                    setAddresses((previous) => previous.map((address) => {
                        const hasServerId = Number(address?.facility_address_id) === Number(addressEditorState.addressId);
                        const hasLocalId = address?.local_id === addressEditorState.localId;
                        if (!hasServerId && !hasLocalId) {
                            return address;
                        }
                        return {
                            ...address,
                            address_type: payload.address_type,
                            street_1: payload.street_1,
                            street_2: payload.street_2,
                            city: payload.city,
                            st: payload.st,
                            zip: payload.zip,
                            country: payload.country,
                        };
                    }));
                }

                handleAddressEditorCancel();
                return;
            }

            if (addressEditorState.mode === 'add') {
                await apiCreateAddress(facilityId, payload);
            } else if (addressEditorState.addressId) {
                await apiUpdateAddress(addressEditorState.addressId, facilityId, payload);
            }

            await fetchFacilityAddresses(facilityId);
            handleAddressEditorCancel();
        } catch (error) {
            console.error(error);
        }
    };

    const openRemoveAddressDialog = (address) => {
        setAddressPendingDelete(address);
        setShowAddressRemoveConfirm(true);
    };

    const handleCancelRemoveAddressDialog = () => {
        setShowAddressRemoveConfirm(false);
        setAddressPendingDelete(null);
    };

    const handleConfirmRemoveAddress = async () => {
        const facilityId = getFacilityIdOrNull();
        const addressId = Number(addressPendingDelete?.facility_address_id);

        try {
            if (!facilityId || isNewFacility || !addressId) {
                setAddresses((previous) => previous.filter((address) => address !== addressPendingDelete));
            } else {
                await apiDeleteAddress(addressId);
                await fetchFacilityAddresses(facilityId);
            }
        } catch (error) {
            console.error(error);
        } finally {
            handleCancelRemoveAddressDialog();
        }
    };

    const normalizeFacilityForSave = (facility) => {
        const patchFacilityInfo = {...(facility || {})};
        ['facility_id', 'created_on'].forEach((field) => delete patchFacilityInfo[field]);
        return patchFacilityInfo;
    };

    const persistNewFacilityRelations = async (newFacilityId) => {
        const phonePromises = phones
            .map((phone) => ({
                phoneTypeId: getPhoneTypeId(phone),
                digits: extractPhoneDigits(phone?.phone ?? phone?.phone_number),
            }))
            .filter((phone) => phone.digits.length === 10)
            .map((phone) => apiCreatePhone(newFacilityId, {
                phone_type: phone.phoneTypeId,
                phone: phone.digits,
            }));

        const addressPromises = addresses
            .map((address) => ({
                addressTypeId: getAddressTypeId(address),
                street1: String(address?.street_1 ?? '').trim(),
                street2: String(address?.street_2 ?? '').trim(),
                city: String(address?.city ?? '').trim(),
                state: String(address?.st ?? '').trim().toUpperCase(),
                zip: String(address?.zip ?? '').trim(),
            }))
            .filter((address) => address.street1 && address.city && address.state && address.zip)
            .map((address) => apiCreateAddress(newFacilityId, {
                address_type: address.addressTypeId,
                street_1: address.street1,
                street_2: address.street2,
                city: address.city,
                st: address.state,
                zip: address.zip,
                country: 'United States',
            }));

        await Promise.all([...phonePromises, ...addressPromises]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValidated = await validateForm();
        if (!isValidated) {
            return;
        }

        const patchFacilityInfo = normalizeFacilityForSave(facilityInfo);

        try {
            if (isNewFacility) {
                const createResult = await apiPostNewFacility(patchFacilityInfo);
                if (!createResult.success) {
                    setErrors(createResult.error || 'Unable to create facility.');
                    return;
                }

                if (!createResult.facilityId) {
                    setErrors('Unable to read new facility ID from create response.');
                    return;
                }

                await persistNewFacilityRelations(createResult.facilityId);
                onSubmit({ ...facilityInfo, id: createResult.facilityId });
                closeModal();
                return;
            }

            await apiPatchFacilityInfo(patchFacilityInfo.id, patchFacilityInfo);
            onSubmit(facilityInfo);
            closeModal();
        } catch (error) {
            console.error(error);
            setErrors(String(error?.message || 'Unable to save facility changes right now.'));
        }
    };

    const handleCancel = (e) => {
        e.preventDefault();
        closeModal();
    };

    const visiblePhones = phones.filter((phone) => {
        const value = phone?.phone ?? phone?.phone_number ?? '';
        return String(value).trim() !== '';
    });

    const visibleAddresses = addresses.filter((address) => {
        const addressValues = [
            address?.street_1,
            address?.street_2,
            address?.city,
            address?.st,
            address?.zip,
        ];

        return addressValues.some((value) => String(value ?? '').trim() !== '');
    });

    return (
        <div className='facility-modal-container'>
            <div className='facility-modal-edit'>
                <button className='modal-close-btn' onClick={handleCancel} aria-label='Close facility details modal'>X</button>
                <form>
                    {isLoadingModalData ? (
                        <div className='facility-skeleton-grid'>
                            <section className='facility-card'>
                                <div className='facility-skeleton-line facility-skeleton-title'></div>
                                <div className='facility-skeleton-row'>
                                    <div className='facility-skeleton-line facility-skeleton-input'></div>
                                    <div className='facility-skeleton-line facility-skeleton-input'></div>
                                </div>
                                <div className='facility-skeleton-row'>
                                    <div className='facility-skeleton-line facility-skeleton-input'></div>
                                    <div className='facility-skeleton-line facility-skeleton-input'></div>
                                </div>
                            </section>
                            <section className='facility-card'>
                                <div className='facility-skeleton-line facility-skeleton-title short'></div>
                                <div className='facility-skeleton-line facility-skeleton-input'></div>
                            </section>
                            <section className='facility-card'>
                                <div className='facility-skeleton-line facility-skeleton-title short'></div>
                                <div className='facility-skeleton-line facility-skeleton-input'></div>
                                <div className='facility-skeleton-line facility-skeleton-input'></div>
                            </section>
                        </div>
                    ) : (
                        <div className='facility-card-grid'>
                            <section className='facility-card'>
                                <div className='facility-card-header'>
                                    <h3>Facility Details</h3>
                                    <div className='facility-toggle'>
                                        <input id='is_authorized' name='is_authorized' type='checkbox' value={facilityInfo.is_authorized} onChange={handleCheckChange} checked={facilityInfo.is_authorized} disabled={isReadOnlyMode}/>
                                        <label htmlFor='is_authorized'>Authorized</label>
                                    </div>
                                </div>
                                <div className='facility-fields-grid two-col'>
                                    {!isNewFacility && (
                                        <div className='form-group wide'>
                                            <label htmlFor='facility_id'>ID: {facilityInfo.id}</label>
                                        </div>
                                    )}
                                    <div className='form-group wide'>
                                        <label htmlFor='facility_name'>Facility Name*</label>
                                        <input name='facility_name' value={facilityInfo.facility_name} onChange={handleChange} readOnly={isReadOnlyMode}/>
                                    </div>
                                    <div className='form-group'>
                                        <label htmlFor='facility_type'>Type</label>
                                        <select
                                            name='description'
                                            value={selectedFacilityTypeValue}
                                            onChange={handleTypeChange}
                                            disabled={facilityTypeSelectDisabled || isReadOnlyMode}
                                        >
                                            {allFacilityTypes.map((facilityType, index) => {
                                                const optionId = facilityType.id ?? facilityType.facility_type_id;
                                                return (
                                                    <option key={optionId ?? index} value={optionId}>
                                                        {facilityType.description}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className='form-group'>
                                        <label htmlFor='customer'>Customer</label>
                                        <select
                                            name='customer'
                                            value={selectedCustomerValue}
                                            onChange={handleCustomerChange}
                                            disabled={customerSelectDisabled || isReadOnlyMode}
                                        >
                                            {allCustomers.map((customer, index) => {
                                                const optionId = customer.id ?? customer.customer_id;
                                                return (
                                                    <option key={optionId ?? index} value={optionId}>
                                                        {customer.customer_name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                            </section>

                            <section className='facility-card'>
                                <div className='facility-contact-card-header'>
                                    <h3>Contact</h3>
                                    {!isReadOnlyMode && (
                                        <button type='button' className='facility-card-link-btn' onClick={openAddPhoneModal}>
                                            <BsPlusLg aria-hidden='true' />
                                            <span>Add Phone</span>
                                        </button>
                                    )}
                                </div>
                                <div className='facility-phone-card-list'>
                                    {visiblePhones.length > 0 ? visiblePhones.map((phone, index) => (
                                        <div className='facility-phone-card' key={phone.facility_phone_id ?? phone.local_id ?? index}>
                                            <div className='facility-phone-card-row'>
                                                <div className='facility-phone-chip'>{getPhoneTypeLabel(phone)}</div>
                                                <div className='facility-phone-value'>{formatPhoneNumber(phone.phone ?? phone.phone_number)}</div>
                                                {!isReadOnlyMode && (
                                                    <div className='facility-card-actions'>
                                                        <button type='button' className='facility-icon-btn' onClick={() => openEditPhoneModal(phone)} aria-label='Edit phone'>
                                                            <BsFillPencilFill />
                                                        </button>
                                                        <button type='button' className='facility-icon-btn danger' onClick={() => openRemovePhoneDialog(phone)} aria-label='Remove phone'>
                                                            <BsFillTrashFill />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )) : <div className='facility-empty-card-spacer'></div>}
                                </div>
                            </section>

                            <section className='facility-card facility-address-card'>
                                <div className='facility-contact-card-header'>
                                    <h3>Addresses</h3>
                                    {!isReadOnlyMode && (
                                        <button type='button' className='facility-card-link-btn' onClick={openAddAddressModal}>
                                            <BsPlusLg aria-hidden='true' />
                                            <span>Add Address</span>
                                        </button>
                                    )}
                                </div>
                                <div className='facility-address-card-list'>
                                    {visibleAddresses.length > 0 ? visibleAddresses.map((address, index) => (
                                        <div className='facility-address-item' key={address.facility_address_id ?? address.local_id ?? index}>
                                            <div className='facility-address-item-header'>
                                                <span className='facility-address-type-badge'>{getAddressTypeLabel(address)}</span>
                                                <div className='facility-address-inline-value'>
                                                    {formatAddressPreview(address).join(' | ')}
                                                </div>
                                                {!isReadOnlyMode && (
                                                    <div className='facility-card-actions'>
                                                        <button type='button' className='facility-icon-btn' onClick={() => openEditAddressModal(address)} aria-label='Edit address'>
                                                            <BsFillPencilFill />
                                                        </button>
                                                        <button type='button' className='facility-icon-btn danger' onClick={() => openRemoveAddressDialog(address)} aria-label='Remove address'>
                                                            <BsFillTrashFill />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )) : <div className='facility-empty-card-spacer'></div>}
                                </div>
                            </section>
                        </div>
                    )}

                    {!isLoadingModalData && !isReadOnlyMode && errors && (
                        <div className='error'>
                            {errors === 'facility_name' ? 'Please include: facility_name' : errors}
                        </div>
                    )}
                    <div className='facility-actions-row'>
                        <button type='button' className='btn btn-cancel' onClick={handleCancel}>{isReadOnlyMode ? 'Close' : 'Close'}</button>
                        {!isLoadingModalData && !isReadOnlyMode && <button type='submit' className='btn' onClick={handleSubmit}>Save</button>}
                    </div>
                </form>
            </div>

            {!isReadOnlyMode && showPhoneRemoveConfirm && (
                <div className='facility-modal-container' onClick={(e) => {
                    if (e.target.className === 'facility-modal-container') {
                        handleCancelRemovePhoneDialog();
                    }
                }}>
                    <div className='facility-modal-edit facility-confirm-modal'>
                        <p className='facility-confirm-message'>
                            Are you sure you want to remove this phone number?
                        </p>
                        <div className='facility-confirm-actions'>
                            <button type='button' className='btn btn-cancel' onClick={handleCancelRemovePhoneDialog}>Cancel</button>
                            <button type='button' className='btn' onClick={handleConfirmRemovePhone}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {!isReadOnlyMode && showPhoneEditor && (
                <div className='facility-modal-container' onClick={(e) => {
                    if (e.target.className === 'facility-modal-container') {
                        handlePhoneEditorCancel();
                    }
                }}>
                    <div className='facility-modal-edit facility-editor-modal'>
                        <h3 className='facility-editor-title'>{phoneEditorState.mode === 'add' ? 'Add Phone' : 'Edit Phone'}</h3>
                        <div className='form-group'>
                            <label htmlFor='facility_phone_type_edit'>Phone Type</label>
                            <select id='facility_phone_type_edit' value={phoneEditorState.phoneTypeId} onChange={handlePhoneEditorTypeChange}>
                                {PHONE_TYPE_OPTIONS.map((phoneType) => (
                                    <option key={phoneType.id} value={phoneType.id}>{phoneType.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='facility_phone_number_edit'>Phone Number</label>
                            <input
                                id='facility_phone_number_edit'
                                type='text'
                                value={phoneEditorState.phone}
                                onChange={handlePhoneEditorPhoneChange}
                                placeholder='(555) 555-5555'
                            />
                        </div>
                        <div className='facility-confirm-actions'>
                            <button type='button' className='btn btn-cancel' onClick={handlePhoneEditorCancel}>Cancel</button>
                            <button type='button' className='btn' onClick={handlePhoneEditorSave} disabled={extractPhoneDigits(phoneEditorState.phone).length !== 10}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {!isReadOnlyMode && showAddressRemoveConfirm && (
                <div className='facility-modal-container' onClick={(e) => {
                    if (e.target.className === 'facility-modal-container') {
                        handleCancelRemoveAddressDialog();
                    }
                }}>
                    <div className='facility-modal-edit facility-confirm-modal'>
                        <p className='facility-confirm-message'>
                            Are you sure you want to remove this address?
                        </p>
                        <div className='facility-confirm-actions'>
                            <button type='button' className='btn btn-cancel' onClick={handleCancelRemoveAddressDialog}>Cancel</button>
                            <button type='button' className='btn' onClick={handleConfirmRemoveAddress}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {!isReadOnlyMode && showAddressEditor && (
                <div className='facility-modal-container' onClick={(e) => {
                    if (e.target.className === 'facility-modal-container') {
                        handleAddressEditorCancel();
                    }
                }}>
                    <div className='facility-modal-edit facility-editor-modal'>
                        <h3 className='facility-editor-title'>{addressEditorState.mode === 'add' ? 'Add Address' : 'Edit Address'}</h3>
                        <div className='form-group'>
                            <label htmlFor='facility_address_type_edit'>Address Type</label>
                            <select
                                id='facility_address_type_edit'
                                value={addressEditorState.addressTypeId}
                                onChange={(event) => handleAddressEditorChange('addressTypeId', Number(event.target.value))}
                            >
                                {addressTypeOptions.map((addressType) => (
                                    <option key={addressType.id} value={addressType.id}>{addressType.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='facility_address_street_1'>Street</label>
                            <input
                                id='facility_address_street_1'
                                type='text'
                                value={addressEditorState.street1}
                                onChange={(event) => handleAddressEditorChange('street1', event.target.value)}
                            />
                        </div>
                        <div className='form-group'>
                            <label htmlFor='facility_address_street_2'>Street 2</label>
                            <input
                                id='facility_address_street_2'
                                type='text'
                                value={addressEditorState.street2}
                                onChange={(event) => handleAddressEditorChange('street2', event.target.value)}
                            />
                        </div>
                        <div className='facility-address-editor-grid'>
                            <div className='form-group'>
                                <label htmlFor='facility_address_city'>City</label>
                                <input
                                    id='facility_address_city'
                                    type='text'
                                    value={addressEditorState.city}
                                    onChange={(event) => handleAddressEditorChange('city', event.target.value)}
                                />
                            </div>
                            <div className='form-group'>
                                <label htmlFor='facility_address_state'>State</label>
                                <select
                                    id='facility_address_state'
                                    value={addressEditorState.state}
                                    onChange={(event) => handleAddressEditorChange('state', event.target.value)}
                                >
                                    <option value=''>Select</option>
                                    {STATE_OPTIONS.map((stateCode) => (
                                        <option key={stateCode} value={stateCode}>{stateCode}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-group'>
                                <label htmlFor='facility_address_zip'>Zip</label>
                                <input
                                    id='facility_address_zip'
                                    type='text'
                                    value={addressEditorState.zip}
                                    onChange={(event) => handleAddressEditorChange('zip', event.target.value)}
                                />
                            </div>
                        </div>
                        <div className='facility-confirm-actions'>
                            <button type='button' className='btn btn-cancel' onClick={handleAddressEditorCancel}>Cancel</button>
                            <button type='button' className='btn' onClick={handleAddressEditorSave} disabled={!isAddressEditorValid()}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ModalFacilityEdit


